import { useState } from 'react';

import { Field, controlClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { formatAmount } from '@core/format/money';
import { formatPct, type DiscountInput } from '@core/bookings/discount';
import { checkGarmentDiscount } from '@core/bookings/garmentSheet';

/**
 * One garment's own concession — "the sherwani is last season's, take ₹500 off that one".
 *
 * <p>
 * <strong>Shared by the counter and the discount drawer</strong>, which is why it takes primitives
 * rather than either screen's row type: the create page holds a `DraftGarment` with no id yet and
 * the drawer holds a `BookingGarment` with one, and a component taking both would grow a union for
 * no gain. Same reasoning `HoldDrawer` gives about itself.
 * </p>
 *
 * <p>
 * <strong>The ceiling shown here is the booking's, and it bounds the whole concession rather than
 * this line.</strong> That is deliberate and the panel says so: the limit is on what the customer
 * gets off in total, so a clerk capped at 5% cannot give 5% on every garment and 5% again over the
 * booking. What this control can honestly promise is that the line alone is inside the limit; the
 * combined check lives where the total is known, and the server checks it again regardless.
 * </p>
 */
export function GarmentDiscountModal({
  name,
  serialCode,
  rate,
  initial,
  ceilingPct,
  currency = '',
  busy = false,
  onClose,
  onApply,
}: {
  name: string;
  serialCode: string;
  /** The garment's own frozen rate. The concession resolves against this and is clamped to it. */
  rate: number;
  initial: DiscountInput;
  ceilingPct: number;
  currency?: string;
  busy?: boolean;
  onClose: () => void;
  onApply: (discount: DiscountInput) => void;
}) {
  // A flat-sum concession comes back as a flat sum: re-seeding it as its effective percentage would
  // silently convert it into one that moves if the rate ever does.
  const [mode, setMode] = useState<'pct' | 'amount'>(initial.pct !== null ? 'pct' : 'amount');
  const [pct, setPct] = useState(initial.pct === null ? '' : String(initial.pct));
  const [amount, setAmount] = useState(initial.amount === null ? '' : String(initial.amount));

  useEscapeKey(onClose);

  const typed = mode === 'pct' ? Number(pct) : Number(amount);
  const entered = (mode === 'pct' ? pct : amount).trim() !== '' && Number.isFinite(typed);

  // The checks are the core's, so the phone's sheet refuses exactly what this refuses.
  const { resolved, problem } = checkGarmentDiscount(
    rate,
    entered
      ? { pct: mode === 'pct' ? typed : null, amount: mode === 'amount' ? typed : null }
      : { pct: null, amount: null },
    ceilingPct,
  );

  const cash = (figure: number) => `${currency}${formatAmount(figure)}`;

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div className="border-[var(--overlay-border)] relative w-full max-w-[380px] rounded border bg-[var(--overlay-surface)] shadow-lg">
        <header className="border-border-color border-b px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">Discount this garment</p>
          <p className="text-default truncate text-[12px]">
            {name} · <span className="font-mono-nums">{serialCode}</span>
          </p>
        </header>

        <div className="flex flex-col gap-3 px-4 py-3">
          <div className="border-border-color grid grid-cols-3 gap-1 rounded border bg-light p-2.5">
            <Figure label="Rate" value={cash(rate)} />
            <Figure label="Off" value={cash(resolved?.amount ?? 0)} tone="brand" />
            <Figure label="Charged" value={cash(Math.max(0, rate - (resolved?.amount ?? 0)))} />
          </div>

          <Field label="Give it as">
            <div className="flex flex-wrap gap-1.5">
              {(
                [
                  ['pct', 'A percentage'],
                  ['amount', 'A fixed amount'],
                ] as const
              ).map(([option, label]) => (
                <button
                  key={option}
                  type="button"
                  disabled={busy}
                  onClick={() => setMode(option)}
                  className={`h-8 cursor-pointer rounded border px-3 text-[12.5px] ${
                    mode === option
                      ? 'border-primary bg-primary text-white'
                      : 'border-border-color bg-white text-gray-900 hover:bg-light'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>

          {mode === 'pct' ? (
            <Field label="Percentage off this garment">
              <input
                type="number"
                onWheel={(event) => event.currentTarget.blur()}
                min={0}
                max={100}
                step="0.01"
                autoFocus
                disabled={busy}
                value={pct}
                onChange={(event) => setPct(event.target.value)}
                className={controlClass}
              />
            </Field>
          ) : (
            <Field
              label="Amount off this garment"
              hint={`Clamped to this garment's ${cash(rate)}.`}
            >
              <input
                type="number"
                onWheel={(event) => event.currentTarget.blur()}
                min={0}
                step="0.01"
                autoFocus
                disabled={busy}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                className={controlClass}
              />
            </Field>
          )}

          {resolved !== null && resolved.amount > 0 && (
            <p className="text-default text-[12px]">
              That is <b className="text-gray-900">{formatPct(resolved.effectivePct)}%</b> off this
              garment.
            </p>
          )}

          {problem !== null && (
            <Notice tone="danger" title="This cannot be applied">
              {problem}
            </Notice>
          )}
        </div>

        <footer className="border-border-color flex items-center gap-2 border-t px-4 py-3">
          {/* Clearing one line, always offered — the same argument the drawer's Remove makes about
              the booking's own concession: undoing a slip must not need a manager. */}
          {(initial.pct !== null || initial.amount !== null) && (
            <Button
              variant="danger"
              disabled={busy}
              onClick={() => onApply({ pct: null, amount: null })}
            >
              Remove
            </Button>
          )}

          <span className="me-auto" />

          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          <Button
            variant="primary"
            shortcut="save"
            loading={busy}
            disabled={!entered || problem !== null || busy}
            onClick={() =>
              onApply(mode === 'pct' ? { pct: typed, amount: null } : { pct: null, amount: typed })
            }
          >
            Apply
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'brand' | undefined;
}) {
  return (
    <div>
      <p className="text-default text-[10.5px] uppercase">{label}</p>
      <p
        className={`font-mono-nums text-[13px] font-semibold ${
          tone === 'brand' ? 'text-primary' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}
